import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Heart, 
  Shield, 
  Lightbulb, 
  Battery, 
  Projector, 
  Radar,
  Camera,
  CameraOff,
  Watch,
  WatchOff,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Users,
  DollarSign,
  Briefcase,
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Globe,
  Moon,
  Sun,
  Loader2
} from 'lucide-react';
import { translations } from '../translations/translations';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [currentLang, setCurrentLang] = useState('en');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const t = translations[currentLang];

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ro', name: 'Română', flag: '🇷🇴' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ar', name: 'العربية', flag: '🇦🇪' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];

  useEffect(() => {
    // Load dark mode preference from localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Close language menu when clicking outside
    const handleClickOutside = (e) => {
      if (!e.target.closest('.lang-menu')) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Форматируем сообщение для Telegram
      const message = `🎯 *Новое сообщение с CAREVA лендинга*\n\n📝 *Имя:* ${formData.name}\n📧 *Email:* ${formData.email}\n👤 *Роль:* ${formData.role}\n💬 *Сообщение:* ${formData.message || 'Не указано'}\n🌐 *Язык:* ${currentLang}\n⏰ *Время:* ${new Date().toLocaleString()}`;
      
      // Отправляем в Telegram через прокси сервер для избежания CORS
      await axios.post(`https://api.telegram.org/bot${process.env.REACT_APP_TELEGRAM_BOT_TOKEN}/sendMessage`, {
        chat_id: process.env.REACT_APP_TELEGRAM_CHAT_ID,
        text: message,
        parse_mode: 'Markdown',
        disable_web_page_preview: true
      });
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({ name: '', email: '', role: '', message: '' });
      }, 5000);
    } catch (error) {
      console.error('Ошибка отправки формы:', error);
      setSubmitError('Произошла ошибка при отправке формы. Пожалуйста, попробуйте еще раз.');
      setTimeout(() => setSubmitError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Fixed Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg' 
            : 'bg-white dark:bg-gray-900 shadow-md'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
  {/* Logo that switches based on theme */}
  <img
    src={isDarkMode ? "/careva-logo-dark.png" : "/careva-logo-light.png"}
    alt="CAREVA Logo"
    className="h-10 w-auto transition-all duration-300 hover:scale-105"
  />
</div>
            
            <ul className="hidden md:flex space-x-8">
              <li>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 font-medium transition-colors duration-300"
                >
                  {t.nav.about}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('features')}
                  className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 font-medium transition-colors duration-300"
                >
                  {t.nav.features}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('investors')}
                  className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 font-medium transition-colors duration-300"
                >
                  {t.nav.investors}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 font-medium transition-colors duration-300"
                >
                  {t.nav.contact}
                </button>
              </li>
            </ul>

            <div className="flex items-center gap-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>

              {/* Language Switcher */}
              <div className="relative lang-menu">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLangMenu(!showLangMenu);
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
                >
                  <Globe className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {languages.find(l => l.code === currentLang)?.flag}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform duration-300 ${showLangMenu ? 'rotate-180' : ''}`} />
                </button>

                {showLangMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 py-2 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setCurrentLang(lang.code);
                          setShowLangMenu(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-purple-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-3 ${
                          currentLang === lang.code ? 'bg-purple-100 dark:bg-gray-700 text-purple-700 dark:text-purple-400' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="font-medium">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-purple-600 to-cyan-400 text-white px-6 py-2.5 rounded-full font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
              >
                {t.nav.preorder}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 overflow-hidden transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              {/* Tagline with 24/7 */}
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight text-gray-900 dark:text-white">
                {t.hero.title}{' '}
                <span className="bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent">
                  {t.hero.titleHighlight}
                </span>
              </h1>

              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                {t.hero.subtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-gradient-to-r from-purple-600 to-cyan-400 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  {t.hero.btnCustomers}
                </button>
                <button 
                  onClick={() => scrollToSection('investors')}
                  className="border-2 border-purple-600 text-purple-600 dark:text-purple-400 dark:border-purple-400 px-8 py-4 rounded-full font-semibold text-lg hover:bg-purple-600 hover:text-white dark:hover:bg-purple-500 transition-all duration-300"
                >
                  {t.hero.btnInvestors}
                </button>
              </div>
            </div>
            
            <div className="relative animate-fade-in-right">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-cyan-400/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_bdf310d7-0483-4f4c-88e4-8bbf5ed25ced/artifacts/dqp3dum0_Gemini_Generated_Image_727zgh727zgh727z%20%285%29.png" 
                alt="CAREVA device in use" 
                className="relative rounded-3xl shadow-2xl w-full hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem vs Solution Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.comparison.title}{' '}
              <span className="bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent">
                {t.comparison.titleHighlight}
              </span>
              ?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t.comparison.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Cameras Card */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-red-100 dark:bg-red-900/30 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Camera className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t.comparison.cameras.title}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.cameras.item1}</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.cameras.item2}</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.cameras.item3}</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.cameras.item4}</span>
                </li>
              </ul>
            </div>

            {/* CAREVA Card - Highlighted */}
            <div className="bg-gradient-to-br from-purple-600 to-cyan-400 rounded-2xl p-8 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 transform scale-105">
              <div className="bg-white/20 backdrop-blur-sm w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Radar className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-6">{t.comparison.careva.title}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                  <span>{t.comparison.careva.item1}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                  <span>{t.comparison.careva.item2}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                  <span>{t.comparison.careva.item3}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                  <span>{t.comparison.careva.item4}</span>
                </li>
              </ul>
            </div>

            {/* Wearables Card */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-red-100 dark:bg-red-900/30 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Watch className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t.comparison.wearables.title}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.wearables.item1}</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.wearables.item2}</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.wearables.item3}</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{t.comparison.wearables.item4}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Product Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.features.title}{' '}
              <span className="bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent">
                {t.features.titleHighlight}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 md:order-1">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-purple-400/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_bdf310d7-0483-4f4c-88e4-8bbf5ed25ced/artifacts/l3v8gbgt_IMG_20260416_044939_599.jpg" 
                alt="CAREVA Device Features" 
                className="relative rounded-3xl shadow-2xl w-full hover:scale-105 transition-transform duration-500"
              />
            </div>

            <div className="space-y-6 order-1 md:order-2">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-x-2">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-yellow-400 to-orange-500 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t.features.feature1.title}</h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {t.features.feature1.desc}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-x-2">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-green-400 to-emerald-600 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Battery className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t.features.feature2.title}</h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {t.features.feature2.desc}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-x-2">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-blue-400 to-indigo-600 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Projector className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t.features.feature3.title}</h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {t.features.feature3.desc}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-x-2">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-purple-600 to-cyan-400 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Radar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t.features.feature4.title}</h4>
                    <p className="text-gray-600 dark:text-gray-300">
                      {t.features.feature4.desc}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Section */}
      <section id="investors" className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">{t.investors.title}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t.investors.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-gradient-to-br from-purple-600 to-purple-400 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent mb-3">
                {t.investors.stat1.value}
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                {t.investors.stat1.desc}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-gradient-to-br from-cyan-400 to-blue-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent mb-3">
                {t.investors.stat2.value}
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                {t.investors.stat2.desc}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-gradient-to-br from-emerald-400 to-green-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent mb-3">
                {t.investors.stat3.value}
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                {t.investors.stat3.desc}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-400 to-red-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent mb-3">
                {t.investors.stat4.value}
              </div>
              <p className="text-gray-600 dark:text-gray-300">
                {t.investors.stat4.desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.testimonials.title}{' '}
              <span className="bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent">
                {t.testimonials.titleHighlight}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {t.testimonials.review1.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-white">{t.testimonials.review1.name}</h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{t.testimonials.review1.location}</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                "{t.testimonials.review1.text}"
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {t.testimonials.review2.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-white">{t.testimonials.review2.name}</h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{t.testimonials.review2.location}</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                "{t.testimonials.review2.text}"
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {t.testimonials.review3.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-white">{t.testimonials.review3.name}</h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{t.testimonials.review3.location}</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                "{t.testimonials.review3.text}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.faq.title}{' '}
              <span className="bg-gradient-to-r from-purple-600 to-cyan-400 bg-clip-text text-transparent">
                {t.faq.titleHighlight}
              </span>
            </h2>
          </div>

          <div className="space-y-4">
            {[t.faq.q1, t.faq.q2, t.faq.q3, t.faq.q4, t.faq.q5].map((faq, index) => (
              <div 
                key={index}
                className="bg-gray-50 dark:bg-gray-800 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 flex justify-between items-center text-left"
                >
                  <span className="font-semibold text-gray-900 dark:text-white text-lg pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`w-6 h-6 text-purple-600 dark:text-purple-400 flex-shrink-0 transition-transform duration-300 ${
                      openFAQ === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    openFAQ === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6 text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-600 to-cyan-400">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              {t.contact.title}
            </h2>
            <p className="text-xl text-white/90">
              {t.contact.subtitle}
            </p>
          </div>

          {!showSuccess ? (
            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-10 shadow-2xl">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-gray-900 dark:text-white font-semibold mb-2">
                    {t.contact.nameLabel} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={t.contact.namePlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-900 dark:text-white font-semibold mb-2">
                    {t.contact.emailLabel} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={t.contact.emailPlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="role" className="block text-gray-900 dark:text-white font-semibold mb-2">
                    {t.contact.roleLabel} *
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">{t.contact.roleSelect}</option>
                    <option value="customer">{t.contact.roleCustomer}</option>
                    <option value="clinic">{t.contact.roleClinic}</option>
                    <option value="investor">{t.contact.roleInvestor}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-900 dark:text-white font-semibold mb-2">
                    {t.contact.messageLabel}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:border-purple-600 dark:focus:border-purple-400 focus:outline-none transition-colors duration-300 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder={t.contact.messagePlaceholder}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-400 text-white py-4 rounded-xl font-semibold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t.contact.submitting || 'Отправка...'}</span>
                    </>
                  ) : (
                    <span>{t.contact.submit}</span>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="bg-green-500 rounded-3xl p-10 shadow-2xl text-center text-white animate-fade-in">
              <CheckCircle2 className="w-20 h-20 mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4">{t.contact.successTitle}</h3>
              <p className="text-lg">
                {t.contact.successMessage}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-black text-white py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              {/* Footer Logo - Always white/inverted */}
              <img 
                src="/careva-logo-dark.png" 
                alt="CAREVA Logo" 
                className="h-10 w-auto mb-4 brightness-150"
              />
              <p className="text-gray-400 dark:text-gray-500 leading-relaxed">
                {t.footer.tagline}
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.navigation}</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 transition-colors duration-300">
                    {t.nav.about}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('features')} className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 transition-colors duration-300">
                    {t.nav.features}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('investors')} className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 transition-colors duration-300">
                    {t.nav.investors}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-400 dark:text-gray-500 hover:text-cyan-400 transition-colors duration-300">
                    {t.nav.contact}
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.contactTitle}</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-400 dark:text-gray-500">
                  <Mail className="w-5 h-5" />
                  <span>contact@careva.one</span>
                </li>
                <li className="flex items-center gap-3 text-gray-400 dark:text-gray-500">
                  <Phone className="w-5 h-5" />
                  <span>+373 79 393 002</span>
                </li>
                <li className="flex items-center gap-3 text-gray-400 dark:text-gray-500">
                  <MapPin className="w-5 h-5" />
                  <span>Iasi, Tomesti, str.Stupinilor 30</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 dark:border-gray-950 pt-8 text-center text-gray-400 dark:text-gray-600">
            <p>{t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;